// src/pages/TherapeuticEpisodesManagementPage.js
import React, { useState, useMemo, useCallback } from 'react'; // Added useMemo, useCallback
import { Box, Typography, Container, Paper, Button, Tabs, Tab, CircularProgress, Alert, Chip, Stack, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup, FormControlLabel, Radio } from '@mui/material'; // Add DialogActions
import { useNavigate, useLocation } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FilterListIcon from '@mui/icons-material/FilterList';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'; // AI Icon
import { useQuery } from '@tanstack/react-query';
import authFetch from '../utils/authFetch';
import logger from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';
import { format, parseISO, differenceInDays } from 'date-fns'; // For formatting dates
import ReportProblemIcon from '@mui/icons-material/ReportProblem';

import AssistantWidget from '../components/AssistantWidget'; // Import AssistantWidget

const episodesListQueryKey = (tenantId, userId, userRole, filterParams) => ['episodesList', tenantId, userId, userRole, filterParams];

const fetchEpisodesByFilter = async ({ queryKey }) => {
    const [, tenantId, userId, userRole, filterParams] = queryKey;
    
    if (!tenantId || !userId || !userRole) {
        logger.warn("fetchEpisodesByFilter: Missing tenantId, userId, or userRole. Skipping fetch.");
        return [];
    }
    
    let statusesToFetch = [];
    let logCategory = "unknown_filter_type";

    if (typeof filterParams === 'string' && filterParams) { 
        logCategory = `cockpit_filter_(${filterParams})`;
        statusesToFetch = filterParams.split(',').map(s => s.trim()).filter(s => s);
    } else if (typeof filterParams === 'object' && filterParams?.statusCategory) { 
        logCategory = `tab_category_(${filterParams.statusCategory})`;
        const { statusCategory } = filterParams;
        // These backend statuses should match your EpisodeStatus enum values if possible
        const BackendStatus = {
            ACTIVE: "active", INVITED: "invited", PENDING: "pending", PENDING_PATIENT_ACCEPTANCE: "pending_patient_acceptance",
            SUSPENDED: "suspended", COMPLETED: "completed", 
            CANCELLED: "cancelled", ARCHIVED: "archived"
        };

        if (statusCategory === 'active') { 
            statusesToFetch = [BackendStatus.ACTIVE, BackendStatus.INVITED, BackendStatus.PENDING, BackendStatus.PENDING_PATIENT_ACCEPTANCE];
        } else if (statusCategory === 'needsBilling') { 
            statusesToFetch = [BackendStatus.COMPLETED];
        } else if (statusCategory === 'attentionRequired') { 
            statusesToFetch = [BackendStatus.SUSPENDED];
        }
    }

    logger.debug(`Workspaceing episodes for tenant ${tenantId}, user ${userId}, role ${userRole}, filter type: ${logCategory}, statuses: [${statusesToFetch.join(', ')}]`);

    if (statusesToFetch.length === 0 && typeof filterParams === 'object') { // Only warn for tab categories if no statuses derived
        logger.warn(`No API statuses derived for tab filter: ${JSON.stringify(filterParams)}. Returning empty list.`);
        return [];
    }
    // If filterParams is a string (from cockpit), and it's empty, the API will likely return all based on other permissions.
    // If statusesToFetch is empty due to an empty string filterParams, let API handle it.

    const params = new URLSearchParams();
    if (statusesToFetch.length > 0) {
        statusesToFetch.forEach(s => params.append('status', s));
    }
    params.append('limit', '100'); // Consider pagination in future

    try {
        const data = await authFetch(`/api/episodes/?${params.toString()}`);
        logger.info(`Workspaceed episodes for filter ${logCategory}:`, data);
        // Add a placeholder for adherencePercentage if not present
        const episodesWithAdherence = (Array.isArray(data) ? data : []).map(ep => ({
            ...ep,
            adherencePercentage: ep.adherencePercentage !== undefined ? ep.adherencePercentage : null // Placeholder for now (null or 75 for visual testing)
        }));
        return episodesWithAdherence;
    } catch (error) {
        logger.error(`Failed to fetch episodes for filter ${logCategory}:`, error);
        throw error; 
    }
};

// Define EpisodeTimelineBar locally for now, ideally this would be a shared component
const EpisodeTimelineBar = ({ startDate, endDate, daysCompleted, totalEpisodeDays, adherencePercentage }) => {
    if (!startDate) return null;
    let progressPercent = 0;
    if (totalEpisodeDays && daysCompleted >= 0) {
        progressPercent = Math.min(100, (daysCompleted / totalEpisodeDays) * 100);
    } else if (daysCompleted > 0 && !totalEpisodeDays) { // Ongoing without a set end date
        progressPercent = 50; // Default to 50% or another visual cue for ongoing
    }

    
    const adherenceScore = adherencePercentage; // No placeholder value needed
    const adherenceColor = adherenceScore === null ? 'grey.400' // Neutral color for N/A
                         : adherenceScore >= 80 ? 'success.main'
                         : adherenceScore >= 50 ? 'warning.main'
                         : 'error.main';
    
    const title = totalEpisodeDays
        ? `${daysCompleted || 0} of ${totalEpisodeDays} days completed. Adherence: ${adherencePercentage !== null ? adherencePercentage + '%' : 'N/A'}`
        : `Ongoing for ${daysCompleted || 0} days. Adherence: ${adherencePercentage !== null ? adherencePercentage + '%' : 'N/A'}`;

    return (
        <Tooltip title={title}>
            <Box sx={{ width: '100%', height: 8, bgcolor: 'grey.300', borderRadius: 1, overflow: 'hidden', position: 'relative', my: 0.5 }}>
                <Box sx={{ width: `${progressPercent}%`, height: '100%', bgcolor: adherenceColor, borderRadius: 'inherit', transition: 'width 0.5s ease-in-out' }} />
            </Box>
        </Tooltip>
    );
};

const EpisodeList = ({ episodes, isLoading, isError, error, onEpisodeClick, onOpenAIAssistantForEpisode, onApplyBundleClick }) => {
    if (isLoading) return <Box sx={{textAlign: 'center', py: 3}}><CircularProgress /></Box>;
    if (isError) return <Alert severity="error" sx={{my: 2}}>Error loading episodes: {error?.message || "Unknown error"}</Alert>;
    if (!episodes || episodes.length === 0) return <Typography sx={{textAlign: 'center', py: 3, color: 'text.secondary', fontStyle: 'italic'}}>No episodes found matching the criteria.</Typography>;

    const formatEpisodeType = (type) => {
        if (!type) return 'N/A';
        return String(type).replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
    };

    const formatDateSafe = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = parseISO(String(dateString)); // Ensure string input
            return format(date, 'P'); // 'P' is like MM/dd/yyyy
        } catch (e) {
            try {
                const parts = String(dateString).split('-');
                if (parts.length === 3) {
                    return format(new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])), 'P');
                }
            } catch (parseError) {
                 logger.warn(`Could not parse date: ${dateString}`, parseError);
            }
            return 'Invalid Date';
        }
    };
    
    const getStatusChipProps = (statusValue) => {
        const statusUpper = String(statusValue).toUpperCase();
        let chipColor = 'default';
        // Consistent with PatientDetail.js EpisodeStatus enum usage where applicable
        if (statusUpper === "ACTIVE") chipColor = 'success';
        else if (statusUpper === "INVITED" || statusUpper === "PENDING" || statusUpper === "PENDING_PATIENT_ACCEPTANCE") chipColor = 'warning';
        else if (statusUpper === "SUSPENDED") chipColor = 'error';
        else if (statusUpper === "COMPLETED") chipColor = 'info';
        
        return {
            label: statusValue ? formatEpisodeType(statusValue) : 'N/A', // Use formatEpisodeType for consistent casing
            size: "small",
            color: chipColor,
            variant: chipColor !== 'default' ? 'filled' : 'outlined',
            sx: { fontWeight: 'medium', height: 'auto', '.MuiChip-label': { py: 0.3, px: 1, fontSize: '0.75rem' } }
        };
    };


    return (
        <Box>
            {episodes.map(ep => {
                const patientProfile = ep.patient_user?.patient_profile;
                let patientIdentifierDisplay = '';
                if (patientProfile?.date_of_birth) {
                    patientIdentifierDisplay = `DOB: ${formatDateSafe(patientProfile.date_of_birth)}`;
                } else if (patientProfile?.mrn) {
                    patientIdentifierDisplay = `MRN: ${patientProfile.mrn}`;
                }

                const assignedMaterialTitles = ep.content_assignments && Array.isArray(ep.content_assignments) 
                    ? ep.content_assignments.map(ca => ca.content_item?.title).filter(Boolean) 
                    : [];
                
                const statusChipProps = getStatusChipProps(ep.status);

                // Calculate days for EpisodeTimelineBar (consistent with PatientDetail's approach)
                const endDate = ep.end_date ? parseISO(String(ep.end_date)) : null;
                const startDate = ep.start_date ? parseISO(String(ep.start_date)) : null;
                let daysRemaining = null;
                let totalEpisodeDays = null;
                let daysCompleted = null;
                if (startDate && endDate) {
                    totalEpisodeDays = differenceInDays(endDate, startDate);
                    daysRemaining = Math.max(0, differenceInDays(endDate, new Date()));
                    daysCompleted = Math.max(0, differenceInDays(new Date(), startDate));
                    if (new Date() > endDate) daysCompleted = totalEpisodeDays;
                } else if (startDate) {
                    daysCompleted = Math.max(0, differenceInDays(new Date(), startDate));
                }
                const isHighRisk = ep.cancellation_risk_score && ep.cancellation_risk_score > 50;
                return (
                    <Paper 
                        key={ep.id} 
                        variant="outlined" 
                        sx={{ p: 2, mb: 2, borderRadius: 2, '&:hover': { boxShadow: 3, borderColor: 'primary.light' }, position: 'relative', borderLeft: isHighRisk ? 4 : 'none', borderLeftColor: isHighRisk ? 'error.main' : 'transparent',}}
                        // onClick handled by button or specific clickable areas if preferred over whole card click
                    >
                        <Typography
                            variant="caption"
                            display="block"
                            title={`SaMD SN: ${ep.id}`}
                            sx={{
                                fontSize: '0.675rem', color: 'text.secondary', fontStyle: 'italic',
                                mb: 0.75, lineHeight: 1.2, textAlign: 'left',
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%'
                            }}
                        >
                            SaMD SN: {ep.id}
                        </Typography>
                        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>   
                            <Typography 
                                variant="subtitle1" 
                                fontWeight="medium" 
                                component="div"
                                onClick={() => onEpisodeClick(ep.patient_user_id, ep.id)}
                                sx={{
                                    flexGrow: 1, 
                                    mr: 1,
                                    cursor:'pointer', 
                                    '&:hover': {color: 'primary.main'},
                                    display: 'flex', // START: Add flex display to align icon
                                    alignItems: 'center'
                                }}
                            >                            
                                {isHighRisk && (
                                    <Tooltip title="High Cancellation Risk Detected">
                                        <ReportProblemIcon color="error" sx={{ mr: 1 }} />
                                    </Tooltip>
                                )}
                                {/* END: Add flex display and conditional icon */}
                                Patient: {ep.patient_user?.first_name || 'N/A'} {ep.patient_user?.last_name || ''}
                                {patientIdentifierDisplay && ` (${patientIdentifierDisplay})`}
                            </Typography>

                            <Tooltip title="AI Assistant for this Episode">
                                <IconButton
                                    size="small"
                                    onClick={() => onOpenAIAssistantForEpisode(ep.id, `${ep.patient_user?.first_name || 'Patient'} ${ep.patient_user?.last_name || ''}`, formatEpisodeType(ep.episode_type))}
                                    aria-label={`AI Assistant for episode ${ep.id}`}
                                    sx={{color: 'text.secondary'}}
                                >
                                    <AutoAwesomeOutlinedIcon fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                            Program: {formatEpisodeType(ep.episode_type)} | Status: <Chip {...statusChipProps} />
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
                            Start Date: {ep.start_date ? formatDateSafe(ep.start_date) : 'N/A'}
                        </Typography>

                        <EpisodeTimelineBar
                            startDate={ep.start_date}
                            endDate={ep.end_date}
                            daysCompleted={daysCompleted}
                            totalEpisodeDays={totalEpisodeDays}
                            adherencePercentage={ep.adherencePercentage} /* Will be null or mock for now */
                        />
                        <Typography variant="caption" display="block" sx={{mb: 1.5}}>
                            Adherence: {ep.adherencePercentage !== null ? `${ep.adherencePercentage}%` : 'N/A (Requires Backend Update)'}
                        </Typography>
                        
                        {assignedMaterialTitles.length > 0 ? (
                            <Box mt={0.5}>
                                <Typography variant="caption" component="div" color="text.secondary" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                                    Assigned Materials:
                                </Typography>
                                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                                    {assignedMaterialTitles.slice(0, 3).map((title, index) => (
                                        <Chip 
                                            key={index} 
                                            label={title} 
                                            size="small" 
                                            variant="outlined" 
                                            title={title}
                                            sx={{maxWidth: '180px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}} 
                                        />
                                    ))}
                                    {assignedMaterialTitles.length > 3 && <Chip label={`+${assignedMaterialTitles.length - 3} more`} size="small" />}
                                </Stack>
                            </Box>
                        ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                No materials assigned.
                            </Typography>
                        )}
                         <Button 
                            size="small" 
                            variant="text" 
                            onClick={() => onEpisodeClick(ep.patient_user_id, ep.id)} 
                            sx={{mt: 1.5, display:'block', ml:'auto', mr:0}}
                         >
                            View Details
                        </Button>
                        <Button 
                            size="small" 
                            variant="contained" 
                            onClick={() => onApplyBundleClick(ep)} 
                            sx={{mt: 1.5, ml: 1, display:'block', float: 'right'}}
                        >
                            Apply Bundle
                        </Button>

                    </Paper>
                );
            })}
        </Box>
    );
};

const TherapeuticEpisodesManagementPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { selectedTenant, user } = useAuth();
    
    const cockpitFilterStatus = location.state?.filterByStatus;
    const cockpitFilterDisplayName = location.state?.filterDisplayName;
    
    const [currentTab, setCurrentTab] = useState(0);
    const [bundleModalOpen, setBundleModalOpen] = useState(false);
    const [selectedEpisodeForBundle, setSelectedEpisodeForBundle] = useState(null);
    const [selectedBundleId, setSelectedBundleId] = useState(null);
    const [isApplying, setIsApplying] = useState(false);

    const tabCategories = ['active', 'needsBilling', 'attentionRequired']; 

    const { data: availableBundles, isLoading: isLoadingBundles } = useQuery({
        // REASON: This query was pointing to a non-existent '/api/therapy-modules' endpoint.
        // It's corrected to call the correct '/api/protocols' endpoint and then filter the
        // results on the client-side, consistent with ProgramManager.js.

        queryKey: ['careBundlesForApplying', selectedTenant?.tenant_id], // More specific queryKey
        queryFn: async () => {
            // 1. Fetch from the correct endpoint.
            const data = await authFetch(`/api/protocols`);
            return Array.isArray(data) ? data : [];
        },
        // 2. Add a 'select' function to filter for only relevant "Care Bundles".
        select: (data) => {
            if (!Array.isArray(data)) return [];
            return data.filter(p => p.protocol_type === 'care_bundle' && p.is_active);
        },

        enabled: !!selectedTenant?.tenant_id,
        staleTime: 1000 * 60 * 15, // Cache bundles for 15 minutes
    });

    const filterForQuery = useMemo(() => cockpitFilterStatus 
        ? cockpitFilterStatus 
        : { statusCategory: tabCategories[currentTab] }, [cockpitFilterStatus, currentTab, tabCategories]);

    const pageTitle = cockpitFilterDisplayName || "Remote Monitoring";

    // State for AssistantWidget
    const [isAssistantWidgetOpen, setIsAssistantWidgetOpen] = useState(false);
    const [assistantContext, setAssistantContext] = useState({
        contextType: null,
        initialMessage: '',
        activeDeepContext: false,
        relatedData: null,
        episodeId: null, // To store the episode ID for context
    });

    const handleChangeTab = (event, newValue) => {
        if (cockpitFilterStatus) {
            navigate(location.pathname, { replace: true, state: {} }); 
            setCurrentTab(newValue); 
        } else {
            setCurrentTab(newValue);
        }
    };

    const { 
        data: episodes, 
        isLoading, 
        isError, 
        error 
    } = useQuery({
        queryKey: episodesListQueryKey(selectedTenant?.tenant_id, user?.id, selectedTenant?.role, filterForQuery),
        queryFn: fetchEpisodesByFilter,
        enabled: !!selectedTenant?.tenant_id && !!user?.id && !!selectedTenant?.role && !!filterForQuery,
        staleTime: 1000 * 30, 
        refetchOnWindowFocus: true,
    });

    const handleEpisodeClick = (patientId, episodeId) => {
        if (patientId && episodeId) {
            logger.info(`Navigating to patient ${patientId}, focused on episode ${episodeId}`);
            navigate(`/patients/${patientId}`, { state: { focusedEpisodeId: episodeId } });
        } else {
            logger.warn("Missing patientId or episodeId for navigation", {patientId, episodeId});
        }
    };

    const handleOpenAIAssistantForEpisode = useCallback((episodeId, patientName, episodeType) => {
        setAssistantContext({
            contextType: 'episode_overview_tem_page', // Specific context type
            initialMessage: `Tell me about episode ${String(episodeId).substring(0,8)}... for ${patientName} (${episodeType}).`,
            activeDeepContext: false, // Or true if you want to fetch deep episode context for this
            relatedData: { 
                source: `Therapeutic Episodes Management Page - Episode ${episodeId}`,
                episodeId: episodeId // Make sure episodeId is part of relatedData if AssistantWidget uses it from there
            },
            episodeId: episodeId // Store episodeId for direct passing if AssistantWidget prop takes it
        });
        setIsAssistantWidgetOpen(true);
    }, []);

    const handleCloseAssistantWidget = () => {
        setIsAssistantWidgetOpen(false);
    };

    const handleApplyBundle = async () => {
        if (!selectedBundleId || !selectedEpisodeForBundle) return;
        setIsApplying(true);
        try {
            await authFetch(`/api/episodes/${selectedEpisodeForBundle.id}/apply-bundle`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ therapy_module_id: selectedBundleId })
            });
            // In a real app, show success snackbar here and refresh data via queryClient.invalidateQueries.
            logger.info(`Successfully applied bundle ${selectedBundleId} to episode ${selectedEpisodeForBundle.id}`);
            setBundleModalOpen(false);
        } catch (err) {
            logger.error("Failed to apply bundle:", err);
            // In a real app, show error snackbar here.
        } finally {
            setIsApplying(false);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/cockpit')} sx={{ mb: 3 }}>
                Back to Cockpit
            </Button>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                {cockpitFilterStatus && <FilterListIcon sx={{ mr: 1, color: 'primary.main' }} />} 
                {pageTitle}
            </Typography>
            
            {!cockpitFilterStatus && ( // Only show tabs if not coming from a direct cockpit filter
                <Paper elevation={1} sx={{ borderRadius: 2, mb: 3, overflow:'hidden' }}>
                    <Tabs 
                        value={currentTab} 
                        onChange={handleChangeTab} 
                        aria-label="Episode status categories" 
                        variant="fullWidth" 
                        indicatorColor="primary" 
                        textColor="primary"
                    >
                        <Tab label="Active & Pending" /> 
                        <Tab label="Needs Billing/Closure" />
                        <Tab label="Attention Required" />
                    </Tabs>
                </Paper>
            )}

            <Box sx={{ pt: cockpitFilterStatus ? 1 : 0 }}>
                <EpisodeList 
                    episodes={episodes} 
                    isLoading={isLoading} 
                    isError={isError} 
                    error={error} 
                    onEpisodeClick={handleEpisodeClick}
                    onOpenAIAssistantForEpisode={handleOpenAIAssistantForEpisode}
                    onApplyBundleClick={(episode) => {
                        setSelectedEpisodeForBundle(episode);
                        setBundleModalOpen(true);
                    }}
                />
            </Box>

            {/* Assistant Widget Dialog */}
            {isAssistantWidgetOpen && selectedTenant?.tenant_id && (
                <Dialog
                    open={isAssistantWidgetOpen}
                    onClose={handleCloseAssistantWidget}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            height: '75vh',
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: 2
                        }
                    }}
                >
                    <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexGrow: 1 }}>
                        <AssistantWidget
                            apiEndpoint="/api/assistant/query"
                            tenantId={selectedTenant.tenant_id}
                            episodeId={assistantContext.episodeId} // Pass episodeId directly
                            contextType={assistantContext.contextType}
                            initialMessage={assistantContext.initialMessage}
                            activeDeepContext={assistantContext.activeDeepContext}
                            relatedData={assistantContext.relatedData}
                            externallyControlledOpen={isAssistantWidgetOpen}
                        />
                    </DialogContent>
                </Dialog>
            )}
            <BundleSelectionModal
                open={bundleModalOpen}
                onClose={() => setBundleModalOpen(false)}
                availableBundles={availableBundles}
                isLoadingBundles={isLoadingBundles}
                selectedEpisode={selectedEpisodeForBundle}
                selectedBundleId={selectedBundleId}
                setSelectedBundleId={setSelectedBundleId}
                onApply={handleApplyBundle}
                isApplying={isApplying}
            />
        </Container>
    );
};

// --- New Bundle Selection Modal Component ---
const BundleSelectionModal = ({
    open, onClose, availableBundles, isLoadingBundles, selectedEpisode, 
    selectedBundleId, setSelectedBundleId, onApply, isApplying 
}) => {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Apply Care Bundle to Episode</DialogTitle>
            <DialogContent dividers>
                <Typography gutterBottom>
                    Select a bundle to apply to patient: <strong>{selectedEpisode?.patient_user?.first_name} {selectedEpisode?.patient_user?.last_name}</strong>
                </Typography>
                {isLoadingBundles ? (
                    <CircularProgress />
                ) : (
                    <RadioGroup value={selectedBundleId} onChange={(e) => setSelectedBundleId(e.target.value)}>
                        {availableBundles && availableBundles.length > 0 ? availableBundles.map((bundle) => (
                            <FormControlLabel
                                key={bundle.id}
                                value={bundle.id}
                                control={<Radio />}
                                label={bundle.name}
                            />
                        )) : <Typography>No bundles available for selection.</Typography>}
                    </RadioGroup>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isApplying}>Cancel</Button>
                <Button onClick={onApply} variant="contained" disabled={!selectedBundleId || isApplying}>
                    {isApplying ? <CircularProgress size={24} /> : "Apply Bundle"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TherapeuticEpisodesManagementPage;